import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NotificationService} from '@services/notification.service';
import {Log} from 'ng2-logger';
import {Notification} from '@models/notification.model';
import {Subscription} from 'rxjs';
import {NavigationStart, Router} from '@angular/router';
import {OperationType} from '@app/tool/model/operationType.model';
import {NotificationType} from '@models/enums/notificationType.enum';
import {PermissionService} from '@app/authentication/services/permission.service';
import {JWTUser} from '@models/jwt-data.model';
import {AuthenticationService} from '@app/authentication/services/authentication.service';
import {SidebarVisibilityService} from '@app/core/services/sidebar-visibility.service';

const URLS = {
    TOOL_REQUESTS_LINES: '/tool-requests-lines',
    TOOL_REQUESTS_FACILITIES: '/tool-requests-facilities',
    TOOL_RETURNS_LINES: '/tool-returns-lines',
    TOOL_RETURNS_FACILITIES: '/tool-returns-facilities',

    LIFT_TOOL_REQUESTS_LINE: '/incoming-tool-requests/lines',
    LIFT_TOOL_REQUESTS_FACILITIES: '/incoming-tool-requests/facilities',
    LIFT_TOOL_RETURNS_LINES: '/incoming-tools-returned/lines',
    LIFT_TOOL_RETURNS_FACILITIES: '/incoming-tools-returned/facilities',

    LINE_SETTER_PENDING_TOOL_REQUESTS: '/pending-tool-requests',

    SERVICE_INCOMING_TICKETS: '/service-tickets',
    CREATE_MANAGE_TICKETS: '/manage-tickets-create',
    UPDATE_MANAGE_TICKETS: '/manage-tickets-update'
};


@Component({
    selector: 'app-sidebar',
    templateUrl: 'sidebar.component.html',
    styleUrls: ['sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {

    _logger = Log.create(SidebarComponent.name);

    @Input()
    currentUser: JWTUser;

    @Input()
    serviceOperations: OperationType[] = [];

    newLineToolRequestsSubscription: Subscription;
    newFacilityToolRequestsSubscription: Subscription;
    newToolReturnsSubscription: Subscription;
    newFacilityToolReturnsSubscription: Subscription;

    newLiftOperatorToIncomingToolRequests: Subscription;
    newLiftOperatorToIncomingFacilityToolRequests: Subscription;
    newLiftOperatorToIncomingToolReturns: Subscription;
    newLiftOperatorToIncomingFacilityToolReturns: Subscription;

    newServiceOperatorRequests: Subscription;
    newLIneSetterRejectionSubscription: Subscription;

    newCreatedTickets: Subscription;
    newUpdatedTickets: Subscription;

    visitedUrls = new Map<string, boolean>();

    incomingLineToolRequests = 0;
    incomingLineToolReturns = 0;
    incomingFacilityToolRequests = 0;
    incomingFacilityToolReturns = 0;
    processLineToolRequests = 0;
    processLineToolReturns = 0;
    processFacilityToolRequests = 0;
    processFacilityToolReturns = 0;

    createdTickets = 0;
    updatedTickets = 0;

    missedNotifications;
    selectedPlantsIds: string[];
    selectedPlantsChangedSubscription: Subscription;
    receivedPlantsSubscription: Subscription;

    selectedToolbarItems: string[] = [];
    toolbarSubscription: Subscription;

    constructor(private notificationService: NotificationService,
                private permissionService: PermissionService,
                private authService: AuthenticationService,
                private toolbarService: SidebarVisibilityService,
                private router: Router) {

        this.router.events.subscribe((res) => {
            if (res instanceof NavigationStart) {
                this.visitedUrls.set(this.router.url, false);
                switch (this.router.url) {
                    case '/ticket-management': {
                        this.createdTickets = 0;
                        this.updatedTickets = 0;
                        break;
                    }
                    case '/tool-requests-lines': {
                        this.incomingLineToolRequests = 0;
                        break;
                    }
                    case '/tool-returns-lines': {
                        this.incomingLineToolReturns = 0;
                        break;
                    }
                    case '/tool-requests-facilities': {
                        this.incomingFacilityToolRequests = 0;
                        break;
                    }
                    case '/tool-returns-facilities': {
                        this.incomingFacilityToolReturns = 0;
                        break;
                    }
                    case '/incoming-tool-requests/lines': {
                        this.processLineToolRequests = 0;
                        break;
                    }
                    case '/incoming-tools-returned/lines': {
                        this.processLineToolReturns = 0;
                        break;
                    }
                    case '/incoming-tool-requests/facilities': {
                        this.processFacilityToolRequests = 0;
                        break;
                    }
                    case '/incoming-tools-returned/facilities': {
                        this.processFacilityToolReturns = 0;
                        break;
                    }
                    default: {

                    }
                }
            }
        });
        this.selectedPlantsChangedSubscription = this.notificationService.selectedPlantsChangedAsObservable.subscribe(() => {
            this.missedNotifications.toolRequests.approveLineRequests = 0;
            this.missedNotifications.toolReturns.approveLineRequests = 0;
            this.missedNotifications.toolRequests.processLineRequests = 0;
            this.missedNotifications.toolReturns.processLineRequests = 0;
            this.resetIncomingNotifications();
        });
    }

    private initUrlVisitationStatus() {
        this.visitedUrls.set(URLS.TOOL_REQUESTS_LINES, false);
        this.visitedUrls.set(URLS.TOOL_REQUESTS_FACILITIES, false);
        this.visitedUrls.set(URLS.TOOL_RETURNS_LINES, false);
        this.visitedUrls.set(URLS.TOOL_RETURNS_FACILITIES, false);

        // Lift Operator
        this.visitedUrls.set(URLS.LIFT_TOOL_REQUESTS_LINE, false);
        this.visitedUrls.set(URLS.LIFT_TOOL_REQUESTS_FACILITIES, false);
        this.visitedUrls.set(URLS.LIFT_TOOL_RETURNS_LINES, false);
        this.visitedUrls.set(URLS.LIFT_TOOL_RETURNS_FACILITIES, false);
        this.visitedUrls.set(URLS.SERVICE_INCOMING_TICKETS, false);
    }

    ngOnInit(): void {
        this.initUrlVisitationStatus();
        this.subscribeToIncomingLineToolRequests();
        this.subscribeToIncomingFacilityToolRequests();
        this.subscribeToIncomingToolReturns();
        this.subscribeToIncomingFacilityToolReturns();
        // Lift operator queues
        this.subscribeLiftOperatorToIncomingToolRequests();

        this.subscribeLiftOperatorToIncomingFacilityToolRequests();
        this.subscribeLiftOperatorToIncomingToolReturns();
        this.subscribeLiftOperatorToIncomingFacilityToolReturns();
        // Service Operator
        this.subscribeServiceOperatorToIncomingFacilityOperations();

        this.subscribeNewTickets();
        this.subscribeUpdatedTickets();
        // Line Setter
        this.subscribeLineSetterToIncomingActions();

        this.receivedPlantsSubscription = this.notificationService.selectedPlantsReceivedFromAPIAsObservable.subscribe(() => {
                this.selectedPlantsIds = this.getSelectedPlants();
                this.getNotifications(this.selectedPlantsIds);
            }
        )

        this.toolbarSubscription = this.toolbarService.sidebarItems.subscribe(items => {
            this.selectedToolbarItems = items;
        });
    }

    ngOnDestroy(): void {
        this.unsubscribeFromAll();
    }

    getNotifications(plantIds: string[]) {
        this.authService.getMissedNotifications(plantIds).subscribe(
            (data) => {
                this._logger.info('getNotifications:: API successfully returned data: ', data);
                this.missedNotifications = data;
                this.resetIncomingNotifications();
            },
            (error) => {
                this._logger.info('getNotifications:: API returned error: ', error);
            }
        )
    }

    resetIncomingNotifications() {
        this.incomingLineToolRequests = 0;
        this.incomingLineToolReturns = 0;
        this.processLineToolRequests = 0;
        this.processLineToolReturns = 0;
    }

    private subscribeToIncomingFacilityToolRequests() {
        this.newFacilityToolRequestsSubscription = this.notificationService
            .facilitiesToolRequestsQueueObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.incomingFacilityToolRequests++;
                        this._logger.info('newFacilityToolRequestsSubscription:: Using notification', value);
                        this.visitedUrls.set(URLS.TOOL_REQUESTS_FACILITIES, true);
                    }
                }, (error) => {
                    this._logger.error('newFacilityToolRequestsSubscription:: Error while processing notification', error);
                });
    }

    public subscribeToIncomingFacilityToolReturns() {
        this.newFacilityToolReturnsSubscription = this.notificationService
            .facilitiesToolReturnsQueueObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (!this.selectedPlantsIds.length || this.selectedPlantsIds.includes(String(value.plantId))) {
                            this.incomingFacilityToolReturns++;
                            this._logger.info('newFacilityToolRetrunsSubscription:: Using notification', value);
                            this.visitedUrls.set(URLS.TOOL_RETURNS_FACILITIES, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newFacilityToolRetrunsSubscription:: Error while processing notification', error);
                });
    }

    private subscribeToIncomingLineToolRequests() {
        this.newLineToolRequestsSubscription = this.notificationService
            .linesToolRequestsQueueObservable.subscribe(
                (value: Notification) => {
                    if (value && value.message !== NotificationType.LINE_TOOL_REQUEST_CANCELED) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (this.missedNotifications && this.missedNotifications.toolRequests) {
                            this.missedNotifications.toolRequests.approveLineRequests = 0;
                        }

                        if (!this.selectedPlantsIds.length || this.selectedPlantsIds.includes(String(value.plantId))) {
                            this.incomingLineToolRequests++;
                        }
                    } else {
                        this.incomingLineToolRequests--;
                    }

                    this._logger.info('newLineToolRequestsSubscription:: Using notification', value);
                    this.visitedUrls.set(URLS.TOOL_REQUESTS_LINES, true);
                }, (error) => {
                    this._logger.error('newLineToolRequestsSubscription:: Error while processing notification', error);
                });
    }

    private subscribeToIncomingToolReturns() {
        this.newToolReturnsSubscription = this.notificationService
            .toolReturnsQueueObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (this.missedNotifications && this.missedNotifications.approveLineRequests) {
                            this.missedNotifications.toolReturns.approveLineRequests = 0;
                        }

                        if (this.selectedPlantsIds.includes(String(value.plantId)) || !this.selectedPlantsIds.length) {
                            this.incomingLineToolReturns++;
                            this._logger.info('newToolReturnsSubscription:: Using notification', value);
                            this.visitedUrls.set(URLS.TOOL_RETURNS_LINES, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newToolReturnsSubscription:: Error while processing notification', error);
                });
    }

    private subscribeLiftOperatorToIncomingToolRequests() {
        this.newLiftOperatorToIncomingToolRequests = this.notificationService
            .liftOperatorRequestQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (this.missedNotifications && this.missedNotifications.processLineRequests) {
                            this.missedNotifications.toolRequests.processLineRequests = 0;
                        }

                        if (this.selectedPlantsIds.includes(String(value.plantId)) || !this.selectedPlantsIds.length) {
                            this.processLineToolRequests++;
                            this._logger.info('newLiftOperatorToIncomingToolRequests:: Using notification', value);
                            this.visitedUrls.set(URLS.LIFT_TOOL_REQUESTS_LINE, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newLiftOperatorToIncomingToolRequests:: Error while processing notification', error);
                });
    }

    private subscribeLiftOperatorToIncomingFacilityToolRequests() {
        this.newLiftOperatorToIncomingFacilityToolRequests = this.notificationService
            .liftOperatorFacilityRequestQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (!this.selectedPlantsIds.length || this.selectedPlantsIds.includes(String(value.plantId))) {
                            this.processFacilityToolRequests++;
                            this._logger.info('newLiftOperatorToIncomingFacilityToolRequests:: Using notification', value);
                            this.visitedUrls.set(URLS.LIFT_TOOL_REQUESTS_FACILITIES, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newLiftOperatorToIncomingFacilityToolRequests:: Error while processing notification', error);
                });
    }

    private subscribeLiftOperatorToIncomingToolReturns() {
        this.newLiftOperatorToIncomingToolReturns = this.notificationService
            .liftOperatorReturnQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (this.missedNotifications && this.missedNotifications.processLineRequests) {
                            this.missedNotifications.toolReturns.processLineRequests = 0;
                        }

                        if (this.selectedPlantsIds.includes(String(value.plantId)) && this.selectedPlantsIds.length) {
                            this.processLineToolReturns++;
                            this._logger.info('newLiftOperatorToIncomingToolReturns:: Using notification', value);
                            this.visitedUrls.set(URLS.LIFT_TOOL_RETURNS_LINES, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newLiftOperatorToIncomingToolReturns:: Error while processing notification', error);
                });
    }

    private subscribeLiftOperatorToIncomingFacilityToolReturns() {
        this.newLiftOperatorToIncomingFacilityToolReturns = this.notificationService
            .liftOperatorFacilityReturnQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (!this.selectedPlantsIds.length || this.selectedPlantsIds.includes(String(value.plantId))) {
                            this.processFacilityToolReturns++;
                            this._logger.info('newLiftOperatorToIncomingFacilityToolReturns:: Using notification', value);
                            this.visitedUrls.set(URLS.LIFT_TOOL_RETURNS_FACILITIES, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newLiftOperatorToIncomingFacilityToolReturns:: Error while processing notification', error);
                });
    }

    private subscribeServiceOperatorToIncomingFacilityOperations() {
        this.newServiceOperatorRequests = this.notificationService
            .serviceOperatorQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this._logger.info('newServiceOperatorRequests:: Using notification', value);
                        this.visitedUrls.set(URLS.SERVICE_INCOMING_TICKETS, true);
                    }
                }, (error) => {
                    this._logger.error('newServiceOperatorRequests:: Error while processing notification', error);
                });
    }

    private subscribeLineSetterToIncomingActions() {
        this.newLIneSetterRejectionSubscription =
            this.notificationService.lineSetterToolRequestQueueAsObservable.subscribe((value: Notification) => {
                if (value) {
                    switch (value.message) {
                        case NotificationType.LINE_TOOL_REQUEST_REJECTED:
                            this._logger.info('newLIneSetterRejectionSubscription:: Tool request got rejected', value);
                            this.visitedUrls.set(URLS.LINE_SETTER_PENDING_TOOL_REQUESTS, true);
                            break
                    }
                }
            }, (error) => {
                this._logger.error('newLIneSetterRejectionSubscription:: Error while processing notification', error);
            });
    }

    getService() {
        return this.visitedUrls.get('/service-tickets');
    }

    private subscribeNewTickets() {
        this.newCreatedTickets = this.notificationService
            .newTicketQueueAsObservable.subscribe(
                (value: Notification) => {
                    if (value) {
                        this.selectedPlantsIds = this.getSelectedPlants();
                        if (!this.selectedPlantsIds.length || this.selectedPlantsIds.includes(String(value.plantId))) {
                            this._logger.info('newTickets:: Using notification', value);
                            this.createdTickets++;
                            this.visitedUrls.set(URLS.SERVICE_INCOMING_TICKETS, true);
                            this.visitedUrls.set(URLS.CREATE_MANAGE_TICKETS, true);
                        }
                    }
                }, (error) => {
                    this._logger.error('newTickets:: Error while processing notification', error);
                });
    }

    private subscribeUpdatedTickets() {
        this.newUpdatedTickets = this.notificationService.updatedNewTicketQueueAsObservable.subscribe(
            (value: Notification) => {
                if (value) {
                    this._logger.info('updateTicket:: Using notification', value);
                    this.updatedTickets++;
                    this.visitedUrls.set(URLS.SERVICE_INCOMING_TICKETS, true);
                    this.visitedUrls.set(URLS.UPDATE_MANAGE_TICKETS, true);
                }
            }
        )
    }

    private unsubscribeFromAll() {
        if (this.newLineToolRequestsSubscription) {
            this.newLineToolRequestsSubscription.unsubscribe();
        }

        if (this.newFacilityToolRequestsSubscription) {
            this.newFacilityToolRequestsSubscription.unsubscribe();
        }

        if (this.newToolReturnsSubscription) {
            this.newToolReturnsSubscription.unsubscribe();
        }
        if (this.newLiftOperatorToIncomingFacilityToolRequests) {
            this.newLiftOperatorToIncomingFacilityToolRequests.unsubscribe();
        }
        if (this.newLiftOperatorToIncomingFacilityToolReturns) {
            this.newLiftOperatorToIncomingFacilityToolReturns.unsubscribe();
        }
        if (this.newLiftOperatorToIncomingToolRequests) {
            this.newLiftOperatorToIncomingToolRequests.unsubscribe();
        }
        if (this.newLiftOperatorToIncomingToolReturns) {
            this.newLiftOperatorToIncomingToolReturns.unsubscribe();
        }
        if (this.newServiceOperatorRequests) {
            this.newServiceOperatorRequests.unsubscribe();
        }
        if (this.newLIneSetterRejectionSubscription) {
            this.newLIneSetterRejectionSubscription.unsubscribe();
        }
        if (this.newCreatedTickets) {
            this.newCreatedTickets.unsubscribe();
        }
        if (this.newUpdatedTickets) {
            this.newUpdatedTickets.unsubscribe();
        }
        if (this.selectedPlantsChangedSubscription) {
            this.selectedPlantsChangedSubscription.unsubscribe();
        }
        if (this.receivedPlantsSubscription) {
            this.receivedPlantsSubscription.unsubscribe();
        }
        if (this.toolbarSubscription) {
            this.toolbarSubscription.unsubscribe();
        }
    }

    hasPermission(requiredPermission) {
        return this.currentUser.permissions && this.currentUser.permissions.indexOf(requiredPermission) > -1;
    }

    resetNewState(url) {
        this.visitedUrls.set(url, false);
    }

    hasSection(section) {
        return this.permissionService.hasPermissionsForSection(this.currentUser, section);
    }

    hasToolbarItem(item: string): boolean {
        if (!this.toolbarService.hasStoredSelection()) {
            return true;
        }
        return this.selectedToolbarItems.includes(item);
    }

    shouldRenderServiceOperatorCategories() {
        return !!this.currentUser &&
            Array.isArray(this.currentUser.permissions) &&
            this.currentUser.permissions.indexOf('PROCESS_TICKETS') > -1;
    }

    removeOfflineNotifications(event) {
        const clickedElement = event.currentTarget;
        const offlineCounter = clickedElement.querySelector('.requests-counter--offline');

        if (clickedElement.hasChildNodes() && offlineCounter && offlineCounter.parentNode === clickedElement) {
            clickedElement.removeChild(offlineCounter);
        }
    }

    getSelectedPlants() {
        const selectedPlants = localStorage.getItem('selectedPlants');
        return selectedPlants ? selectedPlants.split(',') : [];
    }
}
